import { useEffect, useState } from 'react';
import { SeatingClientTagGroupDto } from '@/services/platform/api/model';
import { getSevenRoomsClientTags } from '@/services/platform/data/booking/sevenrooms';
import { Checkbox } from '@/components/ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useLocaleStore } from '@/lib/core';
import { useClientTranslation } from '@/lib/core/i18n';

// ── ClientTagGroups ───────────────────────────────────────────────────────────

interface ClientTagGroupsProps {
  venueNodeKey: string | null | undefined;
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  groupNotes: Record<string, string>;
  onGroupNotesChange: (group: string, note: string) => void;
  onSelectedGroupTagsChange: (groupTags: string[]) => void;
}

export function ClientTagGroups({
  venueNodeKey,
  selectedTags,
  onTagsChange,
  groupNotes,
  onGroupNotesChange,
  onSelectedGroupTagsChange,
}: ClientTagGroupsProps) {
  const t = useClientTranslation();
  const locale = useLocaleStore(s => s.locale);
  const [groups, setGroups] = useState<SeatingClientTagGroupDto[]>([]);

  useEffect(() => {
    if (!venueNodeKey) return;
    getSevenRoomsClientTags(venueNodeKey, locale).then(data => {
      if (data) setGroups(data);
    });
  }, [venueNodeKey, locale]);

  if (!groups.length) return null;

  const noteMaxLength = 2000;

  const computeSelectedGroupTags = (tags: string[], notes: Record<string, string>) =>
    groups
      .filter(group => {
        const groupId = group.group?.id ?? '';
        const groupKey = group.group?.id ?? group.group?.name ?? '';
        return tags.some(t => t.startsWith(`${groupId}:`)) || (notes[groupKey] ?? '').trim().length > 0;
      })
      .map(group => group.group?.id ?? '')
      .filter(Boolean);

  const toggleTag = (tag: string) => {
    const nextTags = selectedTags.includes(tag)
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag];
    onTagsChange(nextTags);
    onSelectedGroupTagsChange(computeSelectedGroupTags(nextTags, groupNotes));
  };

  return (
    <Accordion type="multiple" className="w-full">
      {groups.map((group, i) => {
        const key = group.group?.id ?? group.group?.name ?? String(i);
        const groupName = group.group?.name ?? '';
        const tags = group.tags ?? [];
        const half = Math.ceil(tags.length / 2);
        const cols = [tags.slice(0, half), tags.slice(half)];
        const note = groupNotes[key] ?? '';
        const placeholder = t('App.Labels.BookingGroupNotesPlaceholder', { group: groupName?.toLowerCase() });
        return (
          <AccordionItem key={key} value={key} className="last:border-b-[0.5px]">
            <AccordionTrigger>{groupName}</AccordionTrigger>
            <AccordionContent className="pb-4">
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-2">
                  {cols.map((col, colIdx) => (
                    <div key={colIdx} className="flex min-w-0 flex-col gap-2.5">
                      {col.map(tag => {
                        const tagKey = `${group.group?.id ?? ''}:${tag.id ?? ''}`;
                        return (
                          <button
                            key={tagKey}
                            type="button"
                            onClick={() => toggleTag(tagKey)}
                            className="flex min-w-0 items-center gap-2 text-left"
                          >
                            <Checkbox
                              checked={selectedTags.includes(tagKey)}
                              tabIndex={-1}
                              aria-hidden="true"
                            />
                            <span className="font-pp-neue-montreal min-w-0 text-[12px] tracking-[0.5px] wrap-anywhere text-[#1a1a1a]">
                              {tag.name}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
                <div className="my-4 flex flex-col gap-1">
                  <div className="h-26 border border-[#dfdfdf] px-3.75 py-2">
                    <textarea
                      className="font-pp-neue-montreal h-full w-full resize-none bg-transparent text-[14px] tracking-[0.5px] text-[#1a1a1a] placeholder:text-[#a9a9a9] focus:outline-none"
                      placeholder={placeholder}
                      maxLength={noteMaxLength}
                      value={note}
                      onChange={e => {
                        const nextNotes = { ...groupNotes, [key]: e.target.value };
                        onGroupNotesChange(key, e.target.value);
                        onSelectedGroupTagsChange(computeSelectedGroupTags(selectedTags, nextNotes));
                      }}
                    />
                  </div>
                  <p className="font-pp-neue-montreal text-[12px] tracking-[0.5px] text-[#666]">
                    {note.length}/{noteMaxLength}
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
